import React, { useMemo } from 'react';
import moment from 'moment';
import GridCell from './GridCell';
import SuggestedTask from './SuggestedTask';
import { HolidayFlag } from '../../../Timeline/components/GridComponents/HolidayFlag';

interface Task {
  id: string;
  item_id: number;
  task_title: string;
  project_name: string;
  project_color: string;
  duration: number;
  completion?: number;
  start: string;
  end: string;
  project_id?: string;
}

interface Group {
  id: string;
  title: string;
  rightTitle?: string;
}

interface TimelineGridProps {
  groups: Group[];
  tasks: any;
  suggestedTasks: any;
  renderArr: string[];
  weekdaysArray: string[];
  holidayNotifyList: string[];
  holidayListObj: Record<string, string>;
  boxWidth: number;
  boxheight: number;
  workingDaysIn4Weeks: number;
  searchText: string;
  orgWorkingDays: number[];
  onElementClick: (e: React.MouseEvent | null, ele: Task) => void;
  onDeleteClick: (e: React.MouseEvent | null, tasksData: Task, group: any) => void;
  onDateClick: (e: React.MouseEvent, clickedDate: string, group: any, tasksData: any, con: boolean) => void;
  onAssignSuggestedTaskClick: (tasksData: any, group: any) => void;
  onSuggestions: (more_tasks: any[], group: any) => void;
  checkHoliday: (date1: any, date2: any, holidayNotifyList: any) => any[];
  areTasksOnSameDate: (tasks: Task[]) => boolean;
  isSingleTeamSelected?: boolean;
  isSprintFilter?: boolean;
}

const TimelineGrid: React.FC<TimelineGridProps> = ({
  groups,
  tasks,
  suggestedTasks,
  renderArr,
  weekdaysArray,
  holidayNotifyList,
  holidayListObj,
  boxWidth,
  boxheight,
  workingDaysIn4Weeks,
  searchText,
  orgWorkingDays,
  onElementClick,
  onDeleteClick,
  onDateClick,
  onAssignSuggestedTaskClick,
  onSuggestions,
  checkHoliday,
  areTasksOnSameDate,
  isSingleTeamSelected = false,
  isSprintFilter = false
}) => {

  // Precompute total grid height and empty rows count (no IIFEs in JSX)
  const { totalHeight, emptyRowsNeeded } = useMemo(() => {
    const currentResourcesHeight = groups.length * boxheight;
    const availableHeight = typeof window !== 'undefined' ? window.innerHeight - 140 : 600;
    const empties = Math.max(0, Math.ceil((availableHeight - currentResourcesHeight) / boxheight));
    return {
      totalHeight: (groups.length + Math.min(empties, 40)) * boxheight,
      emptyRowsNeeded: Math.min(empties, 40)
    };
  }, [groups.length, boxheight]);

  return (
    <>
      {/* Render actual resource groups */}
      {groups.map((group, groupIndex: number) => {
        return (
          <div
            id={`group-${groupIndex}`}
            style={{
              width: boxWidth * (workingDaysIn4Weeks),
              height: `${boxheight}px`
            }}
            className="relative flex overflow-x-clip bg-white dark:bg-tertiary-dark border-b border-gray-200 dark:border-gray-600/10"
            key={group.id}
          >
            {renderArr.length > 0 ? renderArr.map((date, index) => (
              <GridCell
                key={index}
                date={date}
                index={index}
                group={group}
                tasks={tasks}
                renderArr={renderArr}
                boxWidth={boxWidth}
                boxheight={boxheight}
                searchText={searchText}
                orgWorkingDays={orgWorkingDays}
                weekdaysArray={weekdaysArray}
                holidayNotifyList={holidayNotifyList}
                checkHoliday={checkHoliday}
                areTasksOnSameDate={areTasksOnSameDate}
                onElementClick={onElementClick}
                onDeleteClick={onDeleteClick}
                onDateClick={onDateClick}
                isSingleTeamSelected={isSingleTeamSelected}
                isSprintFilter={isSprintFilter}
              />
            )) : null}

            {renderArr?.length > 0 ? renderArr.map((date: string, index: number) => {
              const _curDate = date ? moment(date).format('YYYY-MM-DD') : "";
              const ele = suggestedTasks[group.id] ? suggestedTasks[group.id][_curDate] ? suggestedTasks[group.id][_curDate] : "" : "";
              const dateValidate = moment(moment().format('YYYY-MM-DD')).diff(moment(`${date}`).format('YYYY-MM-DD'))
              const isHoliday = checkHoliday(weekdaysArray?.[index], weekdaysArray?.[index + 1], holidayNotifyList);

              if (ele) {
                return (
                  <SuggestedTask
                    key={`${ele.item_id || ele.id}-${ele.task_title}`}
                    task={ele}
                    boxWidth={boxWidth}
                    boxheight={boxheight}
                    searchText={searchText}
                    group={group}
                    index={index}
                    dateValidate={dateValidate}
                    isHoliday={isHoliday}
                    onAssignSuggestedTaskClick={onAssignSuggestedTaskClick}
                    onSuggestions={onSuggestions}
                  />
                );
              }
              return null;
            }) : ""}

            
          </div>
        )
      })}

{/* Holiday Flags - Rendered at grid level */}
      {renderArr && renderArr.map((date, index) => {
        if (!date) return null;

        const nextDate = index < renderArr.length - 1 ? renderArr[index + 1] : null;
        const isHoliday = checkHoliday(date, nextDate, holidayNotifyList);

        if (isHoliday.length > 0) {
          const getConsecutiveHolidays = () => {
            const holidays: any[] = [];
            let currentIndex = index;

            while (currentIndex < renderArr.length) {
              const currentDate = renderArr[currentIndex];
              const nextDate = currentIndex < renderArr.length - 1 ? renderArr[currentIndex + 1] : null;
              const currentHolidays = checkHoliday(currentDate, nextDate, holidayNotifyList);

              if (currentHolidays.length > 0) {
                currentHolidays.forEach((holidayDate: any) => {
                  const holidayInfo = holidayListObj?.[holidayDate];
                  if (holidayInfo) {
                    holidays.push({
                      date: holidayDate,
                      holiday_name: holidayInfo
                    });
                  }
                });
                currentIndex++;
              } else {
                break;
              }
            }
            return holidays;
          };

          const consecutiveHolidays = getConsecutiveHolidays();

          return (
            <HolidayFlag
              key={`holiday-flag-${index}`}
              holidays={consecutiveHolidays}
              date={date}
              viewType="monthly"
              position={{
                right: `${((renderArr.length - 1) - index) * boxWidth}px`,
                top: '0px'
                
              }}

              
              style={{
                height:`${totalHeight}px`,
                top:'0'
              }}
              transform="translateX(-100%)"
            />
          );
        }
        return null;
      })}
      {/* Render empty resource rows to fill viewport */}
      {Array.from({ length: emptyRowsNeeded }).map((_, emptyIndex) => (
        <div
          key={`empty-group-${emptyIndex}`}
          style={{
            width: boxWidth * (workingDaysIn4Weeks),
            height: `${boxheight}px`
          }}
          className="relative flex overflow-x-clip bg-white dark:bg-tertiary-dark border-b border-gray-200 dark:border-gray-600/10"
        >
          {renderArr.length > 0 ? renderArr.map((date, index) => {
            const _curDate = date ? moment(date).format('YYYY-MM-DD') : "";
            const dateValidate = moment(moment().format('YYYY-MM-DD')).diff(moment(`${date}`).format('YYYY-MM-DD'))
            const weekday = moment(_curDate).weekday();
            const nextDate = index < renderArr.length - 1 ? renderArr[index + 1] : null;
            const isHoliday = checkHoliday(date, nextDate, holidayNotifyList);

            return (
              <div
                key={`empty-cell-${emptyIndex}-${index}`}
                className={`relative inline-flex border-r
                  hover:bg-gray-50 dark:hover:bg-gray-800/30 border-gray-200 dark:border-gray-500/10 border-l-[0.5px] 
                  ${(weekday === orgWorkingDays.length && dateValidate !== 0) ? "!border-gray-500/30 !border-r-2" : ""} 
                  ${isHoliday.length ? "!border-r-[1px] !border-r-blue-300" : ""}`}
                style={{
                  width: boxWidth,
                  minHeight: boxheight
                }}
              >
                <div
                  className="w-full h-full"
                // NOTE: Assign task functionality temporarily removed for empty rows
                // To re-enable, uncomment: className="w-full h-full cursor-not-allowed group"
                >
                  {/* 
                  NOTE: Assign task hover UI temporarily removed for easier re-implementation later
                  To re-enable, uncomment the below:
                  
                  <div className="hidden group-hover:block opacity-50">
                    <div
                      className="w-full h-full pl-4 pb-1 pt-1 text-xs bg-[#014737]/50 rounded-lg border-2 border-dotted border-zinc-200 flex justify-center items-center text-white font-semibold font-['Inter'] cursor-not-allowed"
                      style={{
                        width: `${boxWidth}px`,
                        height: `${boxheight}px`,
                        minHeight: `${boxheight}px`
                      }}
                    >
                      Assign task
                    </div>
                  </div>
                  */}
                </div>
              </div>
            );
          }) : ""}
        </div>
      ))}


      {/* Current Date Flag - Rendered at grid level */}
      {renderArr && renderArr.map((date, index) => {
        if (!date) return null; // Skip empty slots from getRenderingDateArr

        const currentFormattedDate = moment(date, 'DD MMM YYYY').format('YYYY-MM-DD');
        const todayFormattedDate = moment().format('YYYY-MM-DD');
        const isToday = currentFormattedDate === todayFormattedDate;

        if (isToday) {
          // Calculate total height including empty rows (same logic as used for empty rows)
          const currentResourcesHeight = groups.length * boxheight;
          const availableHeight = typeof window !== 'undefined' ? window.innerHeight - 140 : 600;
          const emptyRowsNeeded = Math.max(0, Math.ceil((availableHeight - currentResourcesHeight) / boxheight));
          const totalHeight = (groups.length + Math.min(emptyRowsNeeded, 40)) * boxheight;

          return (
            <div
              key={`current-date-flag-${index}`}
              className="absolute"
              style={{
                width: 1,
                left: `${(index * boxWidth)}px`,
                top: 0,
                height: `${totalHeight}px`,
                backgroundColor: '#22c55e',
                zIndex: 46
              }}
            >
              <div
                className="absolute text-xs bg-green-50 dark:bg-[#1b2333] text-black/80 dark:text-white/80 border border-green-500 dark:border-green-400 whitespace-nowrap overflow-hidden text-ellipsis px-2 py-0.5 rounded-r"
                title={`Today (${moment().format('DD MMM YYYY')})`}
              >
                Today
              </div>
            </div>
          );
        }
        return null;
      })}

      
    </>
  );
};

export default TimelineGrid; 